import { ParentProps, Show, createMemo, createSignal, onMount } from 'solid-js'
import { exportForST, PrintRequest, toast, visitorManagementClient } from 'common'
import { visitorCheckin as api } from 'st-app/api'
import { requireVisitorCheckinContext, VisitorCheckinContext, VisitorCheckinContextJsx } from './VisitorCheckinContext'
import styles from './st-visitor-checkin.module.css'
import Wizard from '../st-wizard/st-wizard'
import { PrintStep } from './fragments/print-step'
import { VerificationStep } from './fragments/verification-step'
import { ScanIDStep } from './fragments/scan-id-step'
import { StationRegistrationStep } from './fragments/station-registration-step'
import { MethodChooser } from './fragments/method-chooser'
import { ManualEntryStep } from './fragments/manual-entry-step'
import Button from '../st-button/st-button';


export class VisitorCheckinProps { }

export default function VisitorCheckin(props: ParentProps<VisitorCheckinProps>) {
    const context = new VisitorCheckinContext();

    return (
        <VisitorCheckinContextJsx.Provider value={context}>
            <VisitorCheckinInternal {...props} />
        </VisitorCheckinContextJsx.Provider>
    )
}


function VisitorCheckinInternal(props: ParentProps<VisitorCheckinProps>) {
    const checkin = requireVisitorCheckinContext();
    const [isSubmitting, setIsSubmitting] = createSignal(false);
    const [stationReady, setStationReady] = createSignal(false);

    const bridgeStatusMessage = createMemo(() => {
        const status = checkin.vmBridgeStatus();
        if (!status) return null;
        if (status.status === 'not_running') return 'Visitor management bridge is not running. Please ensure the service is started and try again.';
        if (status.status === 'outdated') return `Visitor management bridge is outdated (${status.currentVersion} → ${status.latestVersion}). Please update the bridge.`;
        return null;
    });

    onMount(async () => {
        const status = await visitorManagementClient.getStatus();
        checkin.setVmBridgeStatus(status);

        if (status.status !== 'running') return;

        const machineInfo = await visitorManagementClient.getMachineInfo();
        checkin.setMachineInfo(machineInfo);

        if (checkin.vmStationGU()) {
            setStationReady(true);
            return;
        }

        const result = await api.CheckStationRegistration(machineInfo.macAddress);
        if (result.isRegistered && result.vmStationGU) {
            checkin.setVmStationGU(result.vmStationGU);
            setStationReady(true);
        }
    });

    const submit = async () => {
        if (isSubmitting())
            return;

        setIsSubmitting(true);
        try {
            if (!checkin.vmGU()) {
                toast.notify('error', 'Visitor ID is missing. Please go back to the Scan ID step and ensure the ID was scanned properly.');
                setIsSubmitting(false);
                return;
            }

            if (!checkin.verificationStatus() || checkin.verificationStatus() === 'pending') {
                toast.notify('error', 'Visitor verification is not complete. Please go back to the Verification step and ensure the visitor has been approved.');
                setIsSubmitting(false);
                return;
            }

            if (checkin.verificationStatus() === 'dangerous') {
                toast.notify('error', 'The visitor has been flagged as dangerous and cannot be checked in. Please go back to the Verification step.');
                setIsSubmitting(false);
                return;
            }

            if (!checkin.reason()) {
                toast.notify('error', 'Check-in reason is required. Please go back to the Print step and select a reason for the visit.');
                setIsSubmitting(false);
                return;
            }

            if (!checkin.visitorType()) {
                toast.notify('error', 'Visitor type is required. Please go back to the Print step and select a visitor type.');
                setIsSubmitting(false);
                return;
            }

            if (checkin.reason() === '6' && !checkin.notes()) {
                toast.notify('error', 'Notes are required when "Other" is selected as the reason for the visit. Please go back to the Print step and enter notes describing the reason for the visit.');
                setIsSubmitting(false);
                return;
            }

            const cellPhone = checkin.cellPhone();
            if (cellPhone && cellPhone.trim()) {
                const digits = cellPhone.replace(/\D/g, '');
                if (digits.length !== 10) {
                    toast.notify('error', 'Cell phone number must be 10 digits. Please enter a valid US phone number (e.g. 555-555-5555).');
                    setIsSubmitting(false);
                    return;
                }
            }

            const email = checkin.email();
            if (email && email.trim()) {
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
                    toast.notify('error', 'Email address is not valid. Please enter a valid email address (e.g. name@example.com).');
                    setIsSubmitting(false);
                    return;
                }
            }

            await api.CompleteCheckin({
                cellPhone: checkin.cellPhone(),
                email: checkin.email(),
                checkInReason: checkin.reason(),
                meetingWith: checkin.meetingWith(),
                notes: checkin.notes(),
                visitorGU: checkin.vmGU()!,
                visitorType: checkin.visitorType(),
                vmStationGU: checkin.vmStationGU(),
            })

            if (checkin.selectedPrinterName()) {
                checkin.print();
            }

            if (import.meta.env.DEV) {
                toast.notify('success', 'Visitor check-in completed successfully. (DEV mode, modal not closed)');
            } else {
                if (window.ST) {
                    window.ST.CloseModal();
                    window.ST.RefreshView();
                }
            }
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while submitting the visitor check-in.');
        }

        setIsSubmitting(false);
    }

    const ensureVerified = async (): Promise<boolean> => {
        if (checkin.isVerifying()) return false;

        if (checkin.verificationStatus() === 'dangerous') {
            return false;
        }

        return true;
    }

    return (
        <div class={styles['st-visitor-checkin']}>
            <Show when={bridgeStatusMessage()}>
                {(msg) => (
                    <div style="padding: 16px; display: flex; flex-direction: column; gap: 8px; align-items: flex-start;">
                        <span style="color: var(--st-danger-color);">{msg()}</span>
                        <Button icon='fas fa-download'>
                            <a download="" href={'dist/SynergyVisitorManagementBridge-Setup.exe'}>Download Latest Installer</a>
                        </Button>
                    </div>
                )}
            </Show>
            <Show when={checkin.vmBridgeStatus()?.status === 'running'}>
                <Show when={stationReady()} fallback={<StationRegistrationStep onRegistered={() => setStationReady(true)} />}>
                    <Wizard onComplete={submit} size='small'>
                        <Wizard.Step title="Check In Method">
                            <Show when={checkin.entryMode()} fallback={<MethodChooser />}>
                                <div style="display:flex; justify-content:flex-end; padding:0 8px 8px;">
                                    <a href="#" onClick={(e) => { e.preventDefault(); checkin.onScan(undefined); checkin.setEntryMode(undefined); }}>
                                        ← change method
                                    </a>
                                </div>
                                <Show when={checkin.entryMode() === 'scan'} fallback={<ManualEntryStep />}>
                                    <ScanIDStep />
                                </Show>
                            </Show>
                        </Wizard.Step>

                        <Wizard.Step title="Verification" onNext={ensureVerified}>
                            <VerificationStep />
                        </Wizard.Step>

                        <Wizard.Step title="Print">
                            <PrintStep />
                        </Wizard.Step>
                    </Wizard>
                </Show>
            </Show>
        </div>
    );
}


VisitorCheckin.defaultProps = new VisitorCheckinProps();

async function printVisitorBadge(visitorGU: string, printerName?: string) {
    try {
        const preferredPrinter = localStorage.getItem('st-visitor-checkin.preferred-printer') || '';
        const printData = await api.GetBadgePrintData(visitorGU);
        const request: PrintRequest = {
            PrinterName: printerName || preferredPrinter,
            Data: {
                GUID: printData.visitorGU!,
                CheckInDateTime: printData.checkInDateTime!,
                FirstName: printData.firstName!,
                LastName: printData.lastName!,
                ImageBase64: printData.photoBase64 || '',
                MeetingWith: printData.meetingWith || '',
                Reason: printData.reason || '',
                VisitorBarCode: printData.visitorGU!,
                VisitorType: printData.visitorType || '',
            }
        }
        const success = await visitorManagementClient.print(request);
        if (!success) {
            toast.notify('error', 'Failed to print visitor badge. Please check the printer and try again.');
            return;
        }

        toast.notify('success', 'Visitor badge printed successfully.');
    } catch (e) {
        console.error(e);
        toast.notify('error', 'An error occurred while printing the visitor badge.');
    }
}

exportForST({
    Actions: {
        VisitorManagement: {
            PrintVisitorBadge: (params: {
                vmVisitorGU: string | {
                    '#cdata': string
                }
            }) => {
                const visitorGU = typeof params.vmVisitorGU === 'string' ? params.vmVisitorGU : params.vmVisitorGU['#cdata'];
                printVisitorBadge(visitorGU);
            },
        }
    }
});